import { useCallback } from 'react';

/**
 * Returns a memoized onKeyDown handler for HoursInput cells that enables
 * arrow-key navigation between cells using pure DOM queries.
 *
 * No store subscriptions — all navigation is resolved at keydown time
 * via `data-task-id` and `data-date` attributes on input elements.
 */
export function useGridKeyboardNavigation(taskId: string, date: string, dates: string[], workType: string) {
    return useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            switch (e.key) {
                case 'ArrowLeft':
                case 'ArrowRight': {
                    handleHorizontal(e, taskId, date, dates, workType);
                    break;
                }
                case 'ArrowUp': {
                    handleVertical(e, date, -1);
                    break;
                }
                case 'ArrowDown':
                case 'Enter': {
                    handleVertical(e, date, 1);
                    break;
                }
            }
        },
        [taskId, date, dates, workType],
    );
}

function handleHorizontal(e: React.KeyboardEvent<HTMLInputElement>, taskId: string, date: string, dates: string[], workType: string) {
    const direction = e.key === 'ArrowRight' ? 1 : -1;
    const dateIndex = dates.indexOf(date);
    if (dateIndex === -1) return;

    const nextDateIndex = dateIndex + direction;

    if (nextDateIndex >= 0 && nextDateIndex < dates.length) {
        // Same row, adjacent date column
        const nextDate = dates[nextDateIndex];
        const target = document.querySelector<HTMLInputElement>(`input[data-task-id="${taskId}"][data-work-type="${workType}"][data-date="${nextDate}"]`);
        if (target) {
            e.preventDefault();
            target.focus();
        }
    } else {
        // Wrap to adjacent row
        const wrapDate = direction === 1 ? dates[0] : dates.at(-1);
        const candidates = Array.from(document.querySelectorAll<HTMLInputElement>(`input[data-date="${wrapDate}"]`)).sort(
            (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
        );
        if (candidates.length === 0) return;

        const currentRect = e.currentTarget.getBoundingClientRect();

        // Find the cell visually below (right wrap) or above (left wrap) the current input
        const target =
            direction === 1
                ? candidates.find(el => el.getBoundingClientRect().top > currentRect.top + 1)
                : candidates.findLast(el => el.getBoundingClientRect().top < currentRect.top - 1);

        if (target) {
            e.preventDefault();
            target.focus();
        }
    }
}

function handleVertical(e: React.KeyboardEvent<HTMLInputElement>, date: string, direction: 1 | -1) {
    const candidates = Array.from(document.querySelectorAll<HTMLInputElement>(`input[data-date="${date}"]`)).sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
    );

    if (candidates.length === 0) return;

    const currentIndex = candidates.indexOf(e.currentTarget);
    if (currentIndex === -1) return;

    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= candidates.length) return;

    e.preventDefault();
    candidates[targetIndex].focus();
}
